import React from 'react';
import { FeeCollections } from '../../../types';
import { FeeAdjustmentState, isAdjustmentIncomplete } from './feeAdjustmentUtils';
import './AdminFeeControls.css';

// ---- Fee adjustment (amount + required reason + optional customer-facing note) ----

interface FeeAdjustmentFieldsProps {
  // Show only when the admin is allowed (admin_capabilities.can_apply_fee_adjustment)
  canApply: boolean;
  value: FeeAdjustmentState;
  onChange: (next: FeeAdjustmentState) => void;
  // Called when the admin finishes editing and the preview should recalculate
  onBlur?: () => void;
  disabled?: boolean;
  // Label + hint differ for cancel (reversed sign: positive = more refund)
  amountLabel?: string;
  hintText?: string;
  amountPlaceholder?: string;
}

export function FeeAdjustmentFields({
  canApply,
  value,
  onChange,
  onBlur,
  disabled,
  amountLabel = 'Fee Adjustment ($)',
  hintText = 'Negative = discount, positive = surcharge.',
  amountPlaceholder = 'e.g. -25.00 (discount) or 15.00 (surcharge)',
}: FeeAdjustmentFieldsProps) {
  if (!canApply) return null;

  const reasonMissing = isAdjustmentIncomplete(value);

  return (
    <div className="admin-fee-adjustment">
      <div className="admin-fee-field">
        <label>{amountLabel}</label>
        <input
          type="number"
          step="0.01"
          placeholder={amountPlaceholder}
          value={value.amount}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, amount: e.target.value })}
          onBlur={onBlur}
        />
        <span className="admin-fee-hint">{hintText}</span>
      </div>

      <div className="admin-fee-field">
        <label>
          Reason for Adjustment {value.amount && parseFloat(value.amount) !== 0 ? <span className="req">*</span> : null}
          <span className="admin-fee-internal-tag">internal</span>
        </label>
        <input
          type="text"
          maxLength={500}
          placeholder="Why the price was adjusted (not shown to customer)"
          value={value.comment}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, comment: e.target.value })}
          onBlur={onBlur}
          className={reasonMissing ? 'error' : ''}
        />
        {reasonMissing && (
          <span className="admin-fee-error">Reason is required when an adjustment amount is entered</span>
        )}
      </div>

      <div className="admin-fee-field">
        <label>Customer-Facing Description</label>
        <input
          type="text"
          maxLength={200}
          placeholder="Shown to customer (e.g. Rate match). Optional."
          value={value.customerNote}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, customerNote: e.target.value })}
          onBlur={onBlur}
        />
      </div>
    </div>
  );
}

// ---- Discount code (apply / recalculate on blur) ----

interface DiscountCodeFieldProps {
  value: string;
  onChange: (code: string) => void;
  // Recalculate the preview (called on blur when the code changed)
  onApply?: () => void;
  // Was a code applied before editing (controls the helper label)?
  hasExisting?: boolean;
  // discount_error.message from the preview response
  error?: string | null;
  // Set when a carried-forward code was silently dropped by re-validation
  silentDropNotice?: string | null;
  disabled?: boolean;
}

export function DiscountCodeField({
  value,
  onChange,
  onApply,
  hasExisting,
  error,
  silentDropNotice,
  disabled,
}: DiscountCodeFieldProps) {
  return (
    <div className="admin-fee-field admin-discount-field">
      <label>Discount code {hasExisting ? <span className="admin-fee-hint">(clear to remove)</span> : null}</label>
      <input
        type="text"
        placeholder="Enter discount code"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        onBlur={onApply}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onApply?.();
          }
        }}
        className={error ? 'error' : ''}
      />
      {error && <span className="admin-fee-error">{error}</span>}
      {!error && silentDropNotice && <span className="admin-fee-notice">{silentDropNotice}</span>}
    </div>
  );
}

// ---- Applied discount / fee-adjustment line items (read from fee_collections) ----

interface AppliedFeeLinesProps {
  feeCollections?: FeeCollections;
}

export function AppliedFeeLines({ feeCollections }: AppliedFeeLinesProps) {
  const discountLines = feeCollections?.discount || [];
  const adjustmentLines = feeCollections?.fee_adjustment || [];

  if (discountLines.length === 0 && adjustmentLines.length === 0) return null;

  return (
    <>
      {discountLines.map((line, i) => (
        <div key={`disc-${i}`} className="fee-row admin-fee-discount">
          <span>{line.fee_description}</span>
          <span>${(line.amount ?? 0).toFixed(2)}</span>
        </div>
      ))}
      {adjustmentLines.map((line, i) => (
        <div
          key={`adj-${i}`}
          className={`fee-row ${(line.amount ?? 0) < 0 ? 'admin-fee-discount' : ''}`}
        >
          <span>{line.fee_description}</span>
          <span>${(line.amount ?? 0).toFixed(2)}</span>
        </div>
      ))}
    </>
  );
}
